import React, { useEffect, useMemo, useRef, useState } from "react";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import "./dashboard.scss";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterIcon from "../../../../assets/icons/filter-icon.svg";
import { ReactComponent as CalendarReportIcon } from "../../../../assets/icons/calendar-report-icon.svg";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { ReactComponent as DashboardDollarIcon } from "../../../../assets/icons/dashboard-dollar.svg";
import { ReactComponent as ChartIcon } from "../../../../assets/icons/chart-icon.svg";
import dayjs, { Dayjs } from "dayjs";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import {
  AlertComponent,
  SearchInputComponent,
} from "../../../../common/components";
import AdminDashboardFilterPanel, {
  AdminDashboardFilterValues,
} from "./admin-dashboard-filter-panel";
import {
  AdminDashboardExportSection,
  exportAdminDashboardReport,
  getAdminDashboardReports,
  updateOrderPayoutStatus,
} from "../../../../api/admin/dashboard.api";
import { getCurrentUserDetails } from "../../../../common/routines/user-operations";
import {
  getAdminEarningsHeaders,
  AdminEarningRow,
  AdminTableSortKey,
  DEFAULT_ADMIN_FILTERS,
  PayoutStatus,
  SortDirection,
} from "./dashboard.helper";
import {
  formatCurrency,
  getSessionToken,
} from "../../../../common/utils/dashboard-utils";
import { useSetLoading } from "../../../../common/hooks/appearance";

const Dashboard: React.FC = () => {
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] =
    useState<AdminDashboardFilterValues>(DEFAULT_ADMIN_FILTERS);
  const [activeTab, setActiveTab] = useState<"provider" | "preneed">(
    "provider",
  );
  const [openStatusRow, setOpenStatusRow] = useState<number | null>(null);
  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    dayjs().startOf("month"),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs().endOf("day"));
  const [isThisMonthSelected, setIsThisMonthSelected] = useState(true);
  const [sortConfig, setSortConfig] = useState<{
    key: AdminTableSortKey | null;
    direction: SortDirection;
  }>({ key: null, direction: "asc" });
  const [orderRevenue, setOrderRevenue] = useState<number>(0);
  const [leadConversion, setLeadConversion] = useState<number>(0);
  const [myEarningsCommission, setMyEarningsCommission] = useState<number>(0);
  const [exportingSection, setExportingSection] =
    useState<AdminDashboardExportSection | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [rows, setRows] = useState<AdminEarningRow[]>([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const clickedInsideDatePicker =
        !!target.closest(".MuiPickersPopper-root") ||
        !!target.closest(".MuiPickersLayout-root") ||
        !!target.closest(".MuiPickersDay-root") ||
        !!target.closest(".MuiDateCalendar-root") ||
        !!target.closest(".MuiDialog-root");

      if (clickedInsideDatePicker) {
        return;
      }

      if (filterWrapRef.current && !filterWrapRef.current.contains(target)) {
        setShowDateFilter(false);
      }

      if (!target.closest(".status-dropdown")) {
        setOpenStatusRow(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const rangeLabel = useMemo(() => {
    if (isThisMonthSelected) {
      return "This Month";
    }
    if (fromDate && toDate) {
      return `${fromDate.format("DD MMM")} - ${toDate.format("DD MMM")}`;
    }
    if (fromDate) {
      return `${fromDate.format("DD MMM")} -`;
    }
    return "This Month";
  }, [fromDate, toDate, isThisMonthSelected]);

  const handleResetThisMonth = () => {
    setFromDate(dayjs().startOf("month"));
    setToDate(dayjs().endOf("day"));
    setIsThisMonthSelected(true);
    setShowDateFilter(false);
  };

  const handleFromDateChange = (newValue: Dayjs | null) => {
    if (!newValue || !newValue.isValid()) return;
    setIsThisMonthSelected(false);
    setFromDate(newValue.startOf("day"));
  };

  const handleToDateChange = (newValue: Dayjs | null) => {
    if (!newValue || !newValue.isValid()) return;
    setIsThisMonthSelected(false);
    setToDate(newValue.endOf("day"));
  };

  const handleStatusChange = async (rowIndex: number, value: PayoutStatus) => {
    setRows((prev) =>
      prev.map((row, index) =>
        index === rowIndex ? { ...row, payoutStatus: value } : row,
      ),
    );
    try {
      const adminSession = getCurrentUserDetails("superAdmin");
      const token = getSessionToken(adminSession);
      const orderId = rows[rowIndex]?.orderId;
      if (!token || !orderId) return;
      setIsLoading(true);
      await updateOrderPayoutStatus(orderId, value, token);
      setAlertMessage("Order payout status updated successfully.");
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(error?.message || "Failed to update payout status.");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const parseValueForSort = (row: AdminEarningRow, key: AdminTableSortKey) => {
    if (
      key === "orderValue" ||
      key === "partnerEarnings" ||
      key === "afpEarnings"
    ) {
      return Number(row[key].replace(/[^0-9.-]+/g, "")) || 0;
    }
    return row[key].toString()?.toLowerCase();
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return rows;

    return [...rows].sort((a, b) => {
      const valueA = parseValueForSort(a, sortConfig.key as AdminTableSortKey);
      const valueB = parseValueForSort(b, sortConfig.key as AdminTableSortKey);

      if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sortConfig]);

  const handleSort = (key: AdminTableSortKey) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };
  const canExportOrderRevenue = orderRevenue > 0;
  const canExportLeadConversion = leadConversion > 0;
  const canExportEarnings = myEarningsCommission > 0;
  const canExportOrders = rows.length > 0;

  const getReportName = (section: string) => {
    switch (section) {
      case "providerMyBookings":
      case "plannerMyBookings":
        return "My Bookings Report";
      case "providerMyServices":
        return "My Services Report";
      case "plannerMyPackages":
        return "My Packages Report";
      case "providerMyEarnings":
      case "plannerMyEarnings":
        return "My Projected Earnings Report";
      case "adminOrderRevenueReport":
        return "Order Revenue Report";
      case "adminLeadConversionRateReport":
        return "Lead Conversion Analytics Report";
      case "adminEarningsReport":
        return "AFP Commission Report";
      case "adminOrdersReport":
        return "Orders Report";
      default:
        return "Report";
    }
  };

  // Helper to check if a filter is applied
  const isFilterApplied = () => {
    return (
      appliedFilters.provider ||
      appliedFilters.providerSearch ||
      appliedFilters.preneedPlanner ||
      appliedFilters.preneedPlannerSearch
    );
  };

  const handleExportReport = async (section: AdminDashboardExportSection) => {
    if (exportingSection) return;

    try {
      setExportingSection(section);
      const adminSession = getCurrentUserDetails("superAdmin");
      const token = getSessionToken(adminSession);
      if (!token) return;

      const timezone =
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
      const queryFrom = (fromDate ?? dayjs().startOf("month")).startOf("day");
      const queryTo = (toDate ?? dayjs()).endOf("day");
      const from = queryFrom.format("YYYY-MM-DD HH:mm:ss");
      const to = queryTo.format("YYYY-MM-DD HH:mm:ss");

      // Add partnerType and partnerId to export params
      const orderPartnerType = activeTab === "preneed" ? "planner" : "provider";
      let partnerType: "provider" | "planner" | undefined = undefined;
      if (appliedFilters.preneedPlanner) partnerType = "planner";
      if (appliedFilters.provider) partnerType = "provider";
      const selectedPartnerIdRaw =
        partnerType === "provider"
          ? appliedFilters.providerSearch
          : partnerType === "planner"
            ? appliedFilters.preneedPlannerSearch
            : undefined;
      const selectedPartnerId = Number(selectedPartnerIdRaw);

      const exportParams: any = {
        section,
        timezone,
        startDate: from,
        endDate: to,
        orderPartnerType,
        ...(partnerType ? { partnerType } : {}),
        ...(Number.isFinite(selectedPartnerId) && selectedPartnerId > 0
          ? { partnerId: selectedPartnerId }
          : {}),
        ...(searchText ? { searchText } : {}),
      };

      const response = await exportAdminDashboardReport(exportParams, token);

      const message =
        response?.data?.message ||
        response?.data?.data?.message ||
        `${getReportName(section)} generated successfully`;
      setAlertMessage(`${message}. Please check your email.`);
      setAlertType("success");
      setAlertOpen(true);
    } catch (error) {
      console.error("Failed to export admin dashboard report:", error);
      const err = error as { response?: { data?: { message?: string } } };
      setAlertMessage(
        err?.response?.data?.message || "Failed to generate admin report",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setExportingSection(null);
    }
  };

  useEffect(() => {
    const fetchAdminDashboardData = async () => {
      setIsLoading(true);
      try {
        const adminSession = getCurrentUserDetails("superAdmin");
        const token = getSessionToken(adminSession);
        if (!token) return;

        const timezone =
          Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
        const queryFrom = (fromDate ?? dayjs().startOf("month")).startOf("day");
        const queryTo = (toDate ?? dayjs()).endOf("day");
        const from = queryFrom.format("YYYY-MM-DD HH:mm:ss");
        const to = queryTo.format("YYYY-MM-DD HH:mm:ss");

        const orderPartnerType =
          activeTab === "preneed" ? "planner" : "provider";

        let partnerType: "provider" | "planner" | undefined = undefined;
        if (appliedFilters.preneedPlanner) partnerType = "planner";
        if (appliedFilters.provider) partnerType = "provider";

        const selectedPartnerIdRaw =
          partnerType === "provider"
            ? appliedFilters.providerSearch
            : partnerType === "planner"
              ? appliedFilters.preneedPlannerSearch
              : undefined;
        const selectedPartnerId = Number(selectedPartnerIdRaw);
        const sortParams = sortConfig.key
          ? { sortBy: sortConfig.key, sortDirection: sortConfig.direction }
          : {};

        const apiParams: any = {
          timezone,
          startDate: from,
          endDate: to,
          orderPartnerType,
          ...(searchText ? { searchText } : {}),
          ...(partnerType ? { partnerType } : {}),
          ...(Number.isFinite(selectedPartnerId) && selectedPartnerId > 0
            ? { partnerId: selectedPartnerId }
            : {}),
          ...sortParams,
        };

        const response = await getAdminDashboardReports(apiParams, token);
        const payload =
          response?.data?.data?.data || response?.data?.data || {};
        const sourceRows = payload?.orders || [];

        const normalizePayoutStatus = (status: unknown): PayoutStatus => {
          const normalized = String(status || "")?.toLowerCase();
          if (normalized === "cancelled") return "Cancelled";
          if (normalized === "pending") return "Pending";
          if (normalized === "paid") return "Paid";
          return "Pending";
        };

        const mappedRows: AdminEarningRow[] = Array.isArray(sourceRows)
          ? sourceRows.map((item: Record<string, unknown>) => {
              const orderNumber = item?.orderNumber || item?.orderId || "-";

              return {
                orderId: Number(item?.orderId),
                partnerName: String(item?.partnerName),
                orderNumber: String(orderNumber),
                orderValue: formatCurrency(Number(item?.orderValue ?? 0)),
                partnerEarnings: formatCurrency(
                  Number(item?.partnerEarnings ?? 0),
                ),
                afpEarnings: formatCurrency(
                  Number(item?.afpEarnings ?? item?.afpCommission ?? 0),
                ),
                payoutStatus: normalizePayoutStatus(item.payoutStatus),
              };
            })
          : [];

        const parseNumber = (value: unknown) => {
          const parsed = Number(value ?? 0);
          return Number.isFinite(parsed) ? parsed : 0;
        };

        setOrderRevenue(parseNumber(payload?.orderRevenue ?? 0));
        setLeadConversion(parseNumber(payload?.leadConversionRate ?? 0));
        setMyEarningsCommission(parseNumber(payload?.afpEarnings ?? 0));
        setRows(mappedRows.length ? mappedRows : []);
      } catch (error) {
        console.error("Failed to fetch admin dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAdminDashboardData();
  }, [
    fromDate,
    toDate,
    appliedFilters,
    activeTab,
    sortConfig.key,
    sortConfig.direction,
    searchText,
  ]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div className="admin-dashboard-screen">
      <ProfileHeader breadcrumbLink={[{ label: "Dashboard" }]} />

      <div className="admin-dashboard-content">
        <div className="admin-dashboard-head-row">
          <h2>Welcome to your dashboard</h2>

          <div className="admin-dashboard-controls">
            <button
              type="button"
              className="filter-button"
              onClick={() => setIsFilterPanelOpen(true)}
            >
              <img src={FilterIcon} alt="Filter Icon" />
              {isFilterApplied() ? (
                <span className="filter-applied-badge"> Filter Applied</span>
              ) : (
                <span>Filter</span>
              )}
            </button>

            <div className="month-filter-wrap" ref={filterWrapRef}>
              <button
                type="button"
                className="month-filter-button"
                onClick={() => setShowDateFilter((prev) => !prev)}
              >
                <CalendarTodayOutlinedIcon fontSize="small" />
                {rangeLabel}
                <KeyboardArrowDownIcon fontSize="small" />
              </button>

              {showDateFilter && (
                <div className="month-filter-popover">
                  <div className="popover-top-row">
                    <button
                      type="button"
                      className={`${isThisMonthSelected ? "active-range-chip selected" : "active-range-chip"}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleResetThisMonth();
                      }}
                    >
                      This Month
                    </button>
                  </div>
                  <div
                    className={`${isThisMonthSelected ? "custom-date-title" : "custom-date-title selected"}`}
                  >
                    Custom Date Range
                  </div>
                  <div className="custom-date-divider" />

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <label>
                      Start Date
                      <DesktopDatePicker
                        value={fromDate}
                        onChange={handleFromDateChange}
                        onAccept={handleFromDateChange}
                        format="DD MMM YYYY"
                        maxDate={toDate ?? dayjs()}
                        disableFuture
                        slots={{ openPickerIcon: CalendarReportIcon }}
                        slotProps={{
                          openPickerButton: {
                            sx: { color: "#36415380" },
                          },
                          textField: {
                            placeholder: "Enter date",
                            size: "small",
                            className: "date-picker-field",
                            fullWidth: true,
                            inputProps: { readOnly: true },
                          },
                        }}
                      />
                    </label>

                    <label>
                      End Date
                      <DesktopDatePicker
                        value={toDate}
                        onChange={handleToDateChange}
                        onAccept={handleToDateChange}
                        format="DD MMM YYYY"
                        minDate={fromDate ?? undefined}
                        maxDate={dayjs()}
                        disableFuture
                        slots={{ openPickerIcon: CalendarReportIcon }}
                        slotProps={{
                          openPickerButton: {
                            sx: { color: "#36415380" },
                          },
                          textField: {
                            placeholder: "Enter date",
                            size: "small",
                            className: "date-picker-field",
                            fullWidth: true,
                            inputProps: { readOnly: true },
                          },
                        }}
                      />
                    </label>
                  </LocalizationProvider>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="admin-metrics-grid">
          <section className="admin-metric-card">
            <div className="admin-metric-head">
              <div className="admin-metric-main">
                <div className="admin-metric-icon">
                  <DashboardDollarIcon />
                </div>
                <div className="admin-metric-copy">
                  <div className="admin-metric-title">Order Revenue</div>
                  <div className="admin-metric-value">{orderRevenue}</div>
                </div>
              </div>
              <button
                type="button"
                className="export-button"
                onClick={() => handleExportReport("adminOrderRevenueReport")}
                disabled={
                  exportingSection === "adminOrderRevenueReport" ||
                  !canExportOrderRevenue
                }
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                Export
              </button>
            </div>
            <p>Total revenue across provider and preneed planner</p>
          </section>

          <section className="admin-metric-card">
            <div className="admin-metric-head">
              <div className="admin-metric-main">
                <div className="admin-metric-icon">
                  <ChartIcon className="title-icon" />
                </div>
                <div className="admin-metric-copy">
                  <div className="admin-metric-title lead-conversion-title">
                    Lead Analytics
                  </div>
                  <div className="admin-metric-value">{leadConversion}%</div>
                </div>
              </div>
              <button
                type="button"
                className="export-button"
                onClick={() =>
                  handleExportReport("adminLeadConversionRateReport")
                }
                disabled={
                  exportingSection === "adminLeadConversionRateReport" ||
                  !canExportLeadConversion
                }
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                Export
              </button>
            </div>
            <p>Overall lead conversion rate</p>
          </section>

          <section className="admin-metric-card">
            <div className="admin-metric-head">
              <div className="admin-metric-main">
                <div className="admin-metric-icon">
                  <DashboardDollarIcon />
                </div>
                <div className="admin-metric-copy">
                  <div className="admin-metric-title">My Earnings</div>
                  <div className="admin-metric-value">
                    {formatCurrency(myEarningsCommission)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="export-button"
                onClick={() => handleExportReport("adminEarningsReport")}
                disabled={
                  exportingSection === "adminEarningsReport" ||
                  !canExportEarnings
                }
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                Export
              </button>
            </div>
            <p>AFP commission earnings</p>
          </section>
        </div>

        <section className="admin-earnings-card">
          <div className="admin-earnings-head">
            <div>
              <h3>MemorialGo Orders</h3>
              <p>
                Orders and earnings breakdown by providers & planners across
                MemorialGo
              </p>
            </div>
            <button
              type="button"
              className="export-button"
              onClick={() => handleExportReport("adminOrdersReport")}
              disabled={
                exportingSection === "adminOrdersReport" || !canExportOrders
              }
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
              Export
            </button>
          </div>

          <div className="admin-earnings-controls">
            <div className="admin-earnings-tabs">
              <button
                type="button"
                className={activeTab === "provider" ? "active" : ""}
                onClick={() => {
                  setActiveTab("provider");
                  setSearchText("");
                }}
              >
                Provider
              </button>
              <button
                type="button"
                className={activeTab === "preneed" ? "active" : ""}
                onClick={() => {
                  setActiveTab("preneed");
                  setSearchText("");
                }}
              >
                Preneed Planner
              </button>
            </div>
            <div className="admin-earnings-search-row">
              <SearchInputComponent
                placeholder={`Search by Order ID, ${activeTab === "provider" ? "Provider" : "Preneed Planner"} name`}
                onSearch={(value: string) => setSearchText(value)}
                isReset={searchText === ""}
              />
            </div>
          </div>

          <TableContainer
            component={Paper}
            elevation={0}
            className="admin-earnings-table-wrap"
          >
            <Table>
              <TableHead>
                <TableRow>
                  {getAdminEarningsHeaders(
                    activeTab === "preneed" ? "planner" : "provider",
                  ).map((header) => (
                    <TableCell key={header.key} component="th">
                      <TableSortLabel
                        active={sortConfig.key === header.key}
                        direction={
                          sortConfig.key === header.key
                            ? sortConfig.direction
                            : "asc"
                        }
                        onClick={() =>
                          handleSort(header.key as AdminTableSortKey)
                        }
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      style={{ fontStyle: "italic", color: "#888" }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRows.map((row, index) => (
                    <TableRow key={`${row.orderNumber}-${index}`}>
                      <TableCell className="provider-name-cell">
                        {row.partnerName}
                      </TableCell>
                      <TableCell>{row.orderNumber}</TableCell>
                      <TableCell>{row.orderValue}</TableCell>
                      <TableCell className="strong-cell">
                        {row.partnerEarnings}
                      </TableCell>
                      <TableCell className="strong-cell">
                        {row.afpEarnings}
                      </TableCell>
                      <TableCell>
                        <div className="status-dropdown">
                          <button
                            type="button"
                            className={`status-pill status-${row.payoutStatus?.toLowerCase()}`}
                            onClick={() =>
                              setOpenStatusRow((prev) =>
                                prev === index ? null : index,
                              )
                            }
                          >
                            <span>{row.payoutStatus}</span>
                            <KeyboardArrowDownIcon
                              className={
                                openStatusRow === index
                                  ? "status-chevron open"
                                  : "status-chevron"
                              }
                              fontSize="small"
                            />
                          </button>

                          {openStatusRow === index && (
                            <div className="status-menu">
                              <div className="status-menu-title">
                                CHANGE STATUS
                              </div>
                              {(
                                [
                                  "Paid",
                                  "Pending",
                                  "Cancelled",
                                ] as PayoutStatus[]
                              ).map((option) => (
                                <button
                                  key={option}
                                  type="button"
                                  className={`status-menu-item status-${option?.toLowerCase()}`}
                                  onClick={() => {
                                    handleStatusChange(index, option);
                                    setOpenStatusRow(null);
                                  }}
                                >
                                  <span>{option}</span>
                                  <span
                                    className={
                                      row.payoutStatus === option
                                        ? "status-radio status-radio-selected"
                                        : "status-radio"
                                    }
                                  />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </section>
      </div>
      <AdminDashboardFilterPanel
        open={isFilterPanelOpen}
        initialValues={appliedFilters}
        onClose={() => setIsFilterPanelOpen(false)}
        onApply={(filters) => {
          setAppliedFilters(filters);
          // setIsFilterPanelOpen(false);
        }}
        isFilterApplied={!!isFilterApplied()}
      />
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default Dashboard;
